<div class="content-blk">
<div class="product-color-selection-container">
  <!-- Header -->
  <div class="page-header">
    <button mat-icon-button (click)="goBack()" class="back-button">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div class="header-content">
      <h2>{{ isEditMode ? 'Edit Product - Colors' : 'Select Product Colors' }}</h2>
      <p class="header-subtitle">{{ isEditMode ? 'Update the colors offered for this product' : 'Choose which colors you want to offer for each product. You can also upload different design variants for specific colors.' }}</p>
    </div>
  </div>

  <!-- Progress Summary -->
  <div class="progress-summary">
    <div class="summary-card">
      <div class="summary-icon">
        <i class="fa fa-palette"></i>
      </div>
      <div class="summary-content">
        <h4>Color Selection Progress</h4>
        <p>{{totalVariants}} color variant{{totalVariants !== 1 ? 's' : ''}} across {{selectedProducts.length}} product{{selectedProducts.length !== 1 ? 's' : ''}}</p>
      </div>
      <div class="summary-count">
        <div class="count-badge">
          {{totalVariants}}
        </div>
        <span>Total Variants</span>
      </div>
    </div>
  </div>

  <!-- Products List -->
  <div class="products-list">
    <div class="product-card" *ngFor="let product of selectedProducts">
      <!-- Product Header -->
      <div class="product-header">
        <div class="product-info">
          <img [src]="product.productImage" alt="{{product.productName}}" class="product-thumbnail">
          <div class="product-details">
            <h3>{{product.productName}}</h3>
            <p class="product-category">{{product.productCategory}}</p>
          </div>
        </div>

        <div class="product-color-count">
          <span class="count-badge" [class.has-colors]="product.selectedColors.length > 0">
            {{product.selectedColors.length}} Color{{product.selectedColors.length !== 1 ? 's' : ''}}
          </span>
        </div>
      </div>

      <!-- Decoration Type Selector (shown only when the product has more than one decoration option) -->
      <div class="decoration-type-section" *ngIf="product.decorations?.length > 1">
        <h4 class="section-title">Decoration Type</h4>
        <select class="imprint-side-select decoration-type-select"
                [(ngModel)]="product.selectedDecorationTypeId"
                (ngModelChange)="onDecorationChange(product)">
          <option *ngFor="let decoration of product.decorations"
                  [ngValue]="decoration.DecorationTypeId">
            {{ decoration.Name }}
          </option>
        </select>
      </div>

      <!-- Color Selection -->
      <div class="color-selection-section">
        <h4 class="section-title">Available Colors</h4>
        <div class="colors-grid">
          <div
            *ngFor="let color of product.availableColors"
            class="color-item"
            [class.color-selected]="isColorSelected(product, color)"
            [class.color-locked]="isColorLocked(product, color)"
            [class.color-preparing]="isColorPreparing(product, color)"
            [matTooltip]="color.Name"
            matTooltipPosition="above"
            (click)="toggleColorSelection(product, color)">
            <div class="color-box" [style.background-color]="color.HtmlColor1 ? (color.HtmlColor1.startsWith('#') ? color.HtmlColor1 : '#' + color.HtmlColor1) : '#ccc'">
              <!-- WI-1521 fix #1: per-swatch spinner while this color's preview/export work is
                   still running in the background, replacing the old full-screen blocking loader. -->
              <i class="fa fa-spinner fa-spin" *ngIf="isColorPreparing(product, color)" title="Preparing this color…"></i>
              <i class="fa fa-lock" *ngIf="isColorLocked(product, color) && !isColorPreparing(product, color)"></i>
              <i class="fa fa-check" *ngIf="isColorSelected(product, color) && !isColorLocked(product, color) && !isColorPreparing(product, color)"></i>
            </div>
            <span class="color-label">{{color.Name}}</span>
          </div>
        </div>
      </div>

      <!-- Imprint Locations (bulk: apply a graphic to all color variants at once) -->
      <div class="imprint-locations-section" *ngIf="product.selectedColors.length > 0">
        <h4 class="section-title">Imprint Locations</h4>

        <!-- Existing imprint entries -->
        <div class="imprint-entry"
             *ngFor="let entry of getProductImprints(product); trackBy: trackByImprintSide">
          <div class="imprint-thumb">
            <img *ngIf="entry.artworkUrl" [src]="entry.artworkUrl" [alt]="entry.displayName" (error)="onImprintThumbError($event)">
            <i class="fa fa-image" *ngIf="!entry.artworkUrl" aria-hidden="true"></i>
          </div>
          <span class="imprint-loc">{{ entry.displayName }}</span>
          <div class="imprint-actions">
            <button type="button" class="imprint-btn"
                    (click)="editImprintDesignForAllColors(product, entry)">
              <i class="fa fa-pencil" aria-hidden="true"></i> Edit Design
            </button>
            <label class="imprint-btn">
              <i class="fa fa-upload" aria-hidden="true"></i> Replace Artwork
              <input type="file" accept=".png,.jpg,.jpeg,.pdf,.svg" hidden
                     (change)="modifyImprintArtwork(product, entry, $event)">
            </label>
            <button type="button" class="imprint-btn imprint-remove" *ngIf="entry.removable"
                    (click)="removeImprintFromAllVariants(product, entry)">
              <i class="fa fa-trash" aria-hidden="true"></i> Remove
            </button>
          </div>
        </div>

        <!-- Add-imprint row -->
        <div class="imprint-add-row" *ngIf="getAvailableImprintSides(product).length > 0">
          <select class="imprint-side-select"
                  [value]="getImprintDraftSide(product.productId)"
                  (change)="onImprintSideSelected(product.productId, $event.target.value)">
            <option value="" disabled>Select imprint location…</option>
            <option *ngFor="let s of getAvailableImprintSides(product); trackBy: trackByAvailableSide" [value]="s.name">
              {{ s.displayName }}
            </option>
          </select>
          <label class="imprint-btn" [class.imprint-btn-disabled]="isImprintUploading(product.productId)">
            <i class="fa fa-upload" aria-hidden="true"></i> Upload Graphic
            <input type="file" accept=".png,.jpg,.jpeg,.pdf,.svg" hidden
                   [disabled]="isImprintUploading(product.productId)"
                   (change)="onImprintFileSelected(product.productId, $event)">
          </label>
          <span class="imprint-file-name" *ngIf="getImprintDraftFileName(product.productId) as fn">
            <i class="fa fa-spinner fa-spin" *ngIf="isImprintUploading(product.productId)" aria-hidden="true"></i>
            {{ fn }}<span *ngIf="isImprintUploading(product.productId)"> — uploading…</span>
          </span>
          <button type="button" class="imprint-btn imprint-apply"
                  [disabled]="isImprintUploading(product.productId)"
                  (click)="applyImprintToAllVariants(product)">
            Add to All Colors
          </button>
        </div>
      </div>

      <!-- Design Variants for Selected Colors (within product card) -->
      <div class="product-design-variants" *ngIf="product.selectedColors.length > 0">
        <h4 class="section-title">Design Variants</h4>
        <div class="variant-row" *ngFor="let variant of product.selectedColors; let variantIdx = index">
          <!-- Color Cell + Add Side dropdown -->
          <div class="color-cell">
            <span class="color-dot-sm" [style.background-color]="variant.colorCode ? (variant.colorCode.startsWith('#') ? variant.colorCode : '#' + variant.colorCode) : '#ccc'"></span>
            <span class="color-text">{{variant.colorName}}</span>
            <select class="add-side-select"
                    *ngIf="getAvailableSidesForVariant(product, variant).length > 0"
                    (change)="addSideToVariant(product, variant, $event.target.value); $event.target.selectedIndex = 0">
              <option value="" disabled selected>+ Add Side</option>
              <option *ngFor="let availSide of getAvailableSidesForVariant(product, variant)" [value]="availSide.name">
                {{availSide.displayName}}
              </option>
            </select>
          </div>

          <!-- Sides in same row -->
          <div class="sides-row">
            <ng-container *ngFor="let side of getVariantSideImages(product, variant); let sideIdx = index">
              <div class="side-box">
                <!-- WI-1524 fix #1: a failed preview used to just render a blank <img> with no
                     signal at all. side.failed shows a visible per-side error + Retry instead
                     (retrySidePreview reuses the same generatePreviewForCurrentSide dispatcher
                     every other preview call site uses).
                     Progressive preview: side.hasImage (a fast placeholder OR the final high-res
                     result) drives the <img>, separately from side.isLoading (whether the slow,
                     full-resolution pipeline is still working) — so a fast placeholder shows
                     immediately with a small "still refining" badge instead of the whole box
                     staying hidden behind a full spinner until the slow pipeline finishes. -->
                <div class="side-preview"
                     [class.loading]="side.isLoading && !side.hasImage"
                     [class.refining]="side.isLoading && side.hasImage"
                     [class.failed]="side.failed">
                  <img [src]="side.url" [alt]="side.name" *ngIf="side.hasImage && !side.failed">
                  <div class="preview-loading" *ngIf="side.isLoading && !side.hasImage">
                    <i class="fa fa-spinner fa-spin"></i>
                  </div>
                  <div class="refining-badge" *ngIf="side.isLoading && side.hasImage" title="Generating final high-resolution preview…">
                    <i class="fa fa-spinner fa-spin"></i>
                    <span>Loading exported image</span>
                  </div>
                  <div class="preview-error" *ngIf="!side.isLoading && side.failed">
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                    <span class="preview-error-text">Preview failed</span>
                    <button type="button" class="btn-retry"
                            (click)="retrySidePreview(product, variant, side.rawSideName)">
                      <i class="fa fa-redo" aria-hidden="true"></i> Retry
                    </button>
                  </div>
                  <button class="btn-edit-preview" *ngIf="side.hasImage"
                          (click)="openEditorForSide(product, variant, side.name)"
                          title="Edit design">
                    <i class="fa fa-pencil"></i>
                  </button>
                </div>
                <!-- WI-1524 fix #1: the print file is a SEPARATE DH export from the on-screen
                     preview above — one can fail while the other succeeds — so this is a distinct
                     "no print file" warning, not folded into the preview-error state above. -->
                <div class="print-export-error" *ngIf="side.printFailed">
                  <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                  <span>No print file for this side</span>
                  <button type="button" class="btn-retry-inline"
                          (click)="retryPrintExport(product, variant, side.rawSideName)">
                    Retry
                  </button>
                </div>
                <!-- Stretched-logo guard: the logo element's frame no longer matches the artwork's
                     aspect ratio, so the print file would export distorted (oval → circle). The
                     export was skipped; re-adding the logo in the editor re-fits the frame. Retry
                     would just re-flag it, so the action here is Edit, not Retry. -->
                <div class="print-export-error" *ngIf="side.printDistorted">
                  <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                  <span>Logo is stretched – open the editor and re-add the logo</span>
                  <button type="button" class="btn-retry-inline"
                          (click)="openEditorForSide(product, variant, side.name)">
                    Edit
                  </button>
                </div>
                <div class="side-bottom">
                  <div class="side-name-row">
                    <span class="side-name-text">{{side.name}}</span>
                    <span class="side-print-dims" *ngIf="getSidePrintDimensions(variant, side.name) as dims">{{ dims.width | number:'1.0-2' }}" &times; {{ dims.height | number:'1.0-2' }}"</span>
                  </div>
                  <div class="side-logo-edit">
                    <div class="mini-logo">
                      <img [src]="getSideLogoUrl(variant, side.name)" alt="Logo">
                    </div>
                    <label class="btn-edit-sm" [for]="'file-' + product.productId + '-' + variantIdx + '-' + sideIdx">
                      <i class="fa fa-pencil"></i>
                    </label>
                    <input [id]="'file-' + product.productId + '-' + variantIdx + '-' + sideIdx"
                           type="file" accept=".png,.jpg,.jpeg,.pdf,.svg" style="display:none"
                           (change)="onSideLogoSelected(product, variant, side.name, $event)">
                  </div>
                </div>
              </div>
            </ng-container>

          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Footer Actions -->
  <div class="footer-actions">
    <div class="footer-summary">
      <strong>{{totalVariants}}</strong> color variant{{totalVariants !== 1 ? 's' : ''}} selected
    </div>
    <div class="footer-buttons">
      <button mat-stroked-button (click)="goBack()" class="btn-secondary">
        <mat-icon>arrow_back</mat-icon>
        Back to Product Selection
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="continueToNextStep()"
        [disabled]="totalVariants === 0 || generatingPreviews.size > 0 || uploadingBackgrounds.size > 0"
        class="btn-continue">
        <ng-container *ngIf="(generatingPreviews.size > 0 || uploadingBackgrounds.size > 0) && getOverallExportProgress() as progress; else readyLabel">
          <span class="btn-continue-label">
            <i class="fa fa-spinner fa-spin"></i>
            <ng-container *ngIf="progress.total > 0; else plainPreparing">
              Finalizing designs&hellip; ({{progress.completed}}/{{progress.total}} ready)
            </ng-container>
            <ng-template #plainPreparing>Finalizing designs&hellip;</ng-template>
          </span>
        </ng-container>
        <ng-template #readyLabel>
          <span class="btn-continue-label">Continue to Pricing &amp; Tags</span>
        </ng-template>
        <mat-icon class="btn-continue-icon">arrow_forward</mat-icon>
      </button>
    </div>
  </div>
</div>

<!-- DesignHuddle Editor Modal -->
<div *ngIf="showEditorModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999;">
  <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7);" (click)="dismissEditorModal()"></div>
  <div style="position: absolute; top: 20px; left: 20px; right: 20px; bottom: 20px; background: #fff; border-radius: 8px; z-index: 10000; display: flex; flex-direction: column; overflow: hidden;">
    <div style="padding: 12px 20px; border-bottom: 1px solid #e0e0e0; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;">
      <h4 style="margin: 0; font-size: 18px;">
        Edit Design: {{editorProductName}}
        <span *ngIf="editorApplyToAllColors" style="font-size: 12px; font-weight: 500; color: #007bff; margin-left: 8px; vertical-align: middle;">
          (applies to all colors on save)
        </span>
      </h4>
      <button (click)="dismissEditorModal()" title="Close without saving"
              style="background: transparent; border: none; cursor: pointer; font-size: 20px; line-height: 1; color: #888; padding: 4px 8px;">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div style="flex: 1; position: relative; overflow: hidden;">
      <div *ngIf="isEditorLoading" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f9f9f9; z-index: 1;">
        <div style="text-align: center;">
          <i class="fa fa-spinner fa-spin" style="font-size: 48px; color: #007bff;"></i>
          <p style="margin-top: 16px; font-size: 16px; color: #555;">Loading editor...</p>
        </div>
      </div>
      <div id="dh-editor-container-color" style="width: 100%; height: 100%;"></div>
    </div>
    <div style="padding: 12px 20px; border-top: 1px solid #e0e0e0; display: flex; justify-content: flex-end; flex-shrink: 0;">
      <button (click)="closeEditorModal()" style="background: #4caf50; color: #fff; border: none; font-size: 14px; cursor: pointer; padding: 8px 24px; border-radius: 4px; font-weight: 500;">Save & Close</button>
    </div>
  </div>
</div>
</div>

<!-- WI-1578: in-page confirm modal — replaces window.confirm() for Remove / Edit Design prompts -->
<div *ngIf="showConfirmModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 10100; display: flex; align-items: center; justify-content: center;">
  <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.6);" (click)="onConfirmModalOverlayClick()"></div>
  <div style="position: relative; background: #fff; border-radius: 8px; width: 440px; max-width: calc(100% - 32px); box-shadow: 0 8px 32px rgba(0,0,0,0.25); overflow: hidden;">
    <div style="padding: 20px 24px 0; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;">
      <h4 style="margin: 0 0 12px; font-size: 17px; color: #222; display: flex; align-items: center; gap: 8px;">
        <i class="fa fa-exclamation-triangle" *ngIf="confirmModalWarn" style="color: #e6a700;" aria-hidden="true"></i>
        {{ confirmModalTitle }}
      </h4>
      <button type="button" (click)="confirmModalClose()" title="Close"
              style="background: transparent; border: none; cursor: pointer; font-size: 18px; line-height: 1; color: #888; padding: 2px 4px; flex-shrink: 0;">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div style="padding: 0 24px;">
      <p *ngFor="let line of confirmModalMessageLines"
         [style.font-size]="confirmModalCompactText ? '12.5px' : '14px'"
         style="margin: 0 0 10px; color: #555; line-height: 1.5;">{{ line }}</p>
    </div>
    <div style="padding: 16px 24px 20px; display: flex; justify-content: flex-end; gap: 10px;">
      <button type="button" (click)="confirmModalDecline()"
              style="background: #fff; color: #555; border: 1px solid #d0d0d0; font-size: 14px; cursor: pointer; padding: 8px 18px; border-radius: 4px; font-weight: 500;">
        {{ confirmModalCancelLabel }}
      </button>
      <button type="button" (click)="confirmModalAccept()"
              [style.background]="confirmModalDestructive ? '#dc3545' : '#007bff'"
              style="color: #fff; border: none; font-size: 14px; cursor: pointer; padding: 8px 18px; border-radius: 4px; font-weight: 500;">
        {{ confirmModalConfirmLabel }}
      </button>
    </div>
  </div>
</div>

<!-- Fetching Project Data Loader -->
<div *ngIf="isFetchingEditorMetadata" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 10001; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center;">
  <div style="background: #fff; border-radius: 12px; padding: 32px 48px; text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.3);">
    <i class="fa fa-spinner fa-spin" style="font-size: 40px; color: #007bff;"></i>
    <p style="margin-top: 16px; font-size: 16px; color: #333; font-weight: 500;">Saving design data...</p>
    <p style="margin-top: 4px; font-size: 13px; color: #888;">Please wait while we fetch your project details.</p>
  </div>
</div>
